import { useState, useEffect } from 'react';
import { MIDASProject } from '../../types';
import { useProjectStore } from '../stores/projectStore';
import { loadProject, saveProject } from '../services/storageService';
import logger from '../utils/logger';

/**
 * プロジェクトデータを管理するカスタムフック
 * Web版用に修正
 */
export function useProject(projectId: string) {
  // ProjectStoreから直接projectを取得
  const project = useProjectStore((state) => state.project);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // プロジェクトを読み込む
  useEffect(() => {
    const loadProjectData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Web版: ProjectStoreから直接取得
        // TODO: OPFSやIndexedDBから読み込む実装を追加
        const currentProject = useProjectStore.getState().project;
        
        if (currentProject && currentProject.id === projectId) {
          // プロジェクトはメモリにあるが、データセットが空の場合はOPFSから再読み込み
          const datasetsCount = Object.keys(currentProject.datasets || {}).length;

          if (datasetsCount === 0) {
            logger.info('[useProject] Project in memory but has no datasets, reloading from OPFS:', {
              projectId,
              datasetsCount
            });

            // OPFSから再読み込みを試みる
            const filename = `${projectId}.mds`;
            try {
              const savedProject = await loadProject(filename);
              if (savedProject && Object.keys(savedProject.datasets || {}).length > 0) {
                logger.info('[useProject] Project reloaded from OPFS with datasets:', {
                  projectId,
                  datasetsCount: Object.keys(savedProject.datasets || {}).length
                });
                // ストアに読み込む
                useProjectStore.getState().loadProject(savedProject);
              } else {
                logger.warn('[useProject] OPFS project also has no datasets or not found');
              }
            } catch (opfsError) {
              logger.warn('[useProject] Failed to reload from OPFS:', opfsError);
            }
          } else {
            logger.info('[useProject] Project already loaded with datasets:', {
              projectId,
              datasetsCount
            });
          }
        } else {
          // プロジェクトがストアにない場合、OPFSから読み込みを試みる
          logger.info('[useProject] Project not in store, trying to load from OPFS:', projectId);

          // プロジェクトIDからファイル名を生成（saveProjectと同じ形式）
          const filename = `${projectId}.mds`;

          try {
            const savedProject = await loadProject(filename);
            if (savedProject) {
              logger.info('[useProject] Project loaded from OPFS:', {
                projectId,
                datasetsCount: Object.keys(savedProject.datasets || {}).length
              });
              // ストアに読み込む
              useProjectStore.getState().loadProject(savedProject);
            } else {
              logger.warn('[useProject] Project not found in OPFS:', projectId);
              setError(`Project ${projectId} not found`);
            }
          } catch (opfsError) {
            logger.warn('[useProject] Failed to load from OPFS:', opfsError);
            setError(`Project ${projectId} not found`);
          }
        }
      } catch (err) {
        logger.error('Error loading project:', err);
        setError(err instanceof Error ? err.message : 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      loadProjectData();
    }
  }, [projectId]);

  // プロジェクトを更新する関数
  const updateProject = async (updatedProject: MIDASProject) => {
    try {
      // ProjectStoreを更新
      useProjectStore.getState().loadProject(updatedProject);

      // OPFSに保存
      await saveProject(updatedProject);
      logger.info('[useProject] Project updated and saved to OPFS:', updatedProject.id);
    } catch (err) {
      logger.error('Error updating project:', err);
    }
  };

  return {
    project,
    loading,
    error,
    updateProject
  };
}